import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TurnstileWidget, { type TurnstileWidgetHandle } from './TurnstileWidget';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { registerUser } from '../userSlice';
import {
  closeAccountModal,
  selectAccountModal,
  setMode,
  setPendingEmail,
} from '../accountModalSlice';
import GoogleButton, { isGoogleLoginEnabled } from './GoogleButton';
import PasswordField from './PasswordField';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const ERROR_KEYS: Record<string, string> = {
  email_taken: 'auth.errors.email_taken',
  use_google_login: 'auth.errors.useGoogleLogin',
  password_setup_required: 'auth.errors.passwordSetupRequired',
  recaptcha_failed: 'auth.errors.recaptcha_failed',
  server: 'auth.errors.server',
  network: 'auth.errors.network',
  unknown: 'auth.errors.unknownRegister',
};

type RegisterRejectedPayload = string | {
  code?: string;
  fieldErrors?: Record<string, string[]>;
};

const SERVER_FIELD_TO_FORM: Record<string, string> = {
  first_name: 'firstName',
  last_name: 'lastName',
  email: 'email',
  phone: 'phone',
  password: 'password',
};

const SERVER_FIELD_FALLBACKS: Record<string, string> = {
  firstName: 'auth.validation.firstNameRequired',
  lastName: 'auth.validation.lastNameRequired',
  email: 'auth.validation.emailInvalid',
  phone: 'auth.validation.phoneRequired',
  password: 'auth.validation.passwordMin8',
};

function getRegisterError(payload: unknown): { code: string; fieldErrors?: Record<string, string[]> } {
  if (typeof payload === 'string') return { code: payload };
  if (payload && typeof payload === 'object') {
    const rejected = payload as Exclude<RegisterRejectedPayload, string>;
    return {
      code: typeof rejected.code === 'string' ? rejected.code : 'unknown',
      fieldErrors: rejected.fieldErrors,
    };
  }
  return { code: 'unknown' };
}

function mapServerFieldErrors(
  fieldErrors: Record<string, string[]>,
  translate: (key: string) => string,
): Record<string, string> {
  const next: Record<string, string> = {};

  for (const [serverField, messages] of Object.entries(fieldErrors)) {
    const formField = SERVER_FIELD_TO_FORM[serverField];
    if (!formField) continue;
    next[formField] = messages[0] || translate(SERVER_FIELD_FALLBACKS[formField]);
  }

  return next;
}

export default function RegisterPanel() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { restaurantUuid } = useParams<{ restaurantUuid: string }>();
  const { pendingEmail, prefill, redirectTo } = useAppSelector(selectAccountModal);
  // SC-1013 : proposée en fin de parcours de commande, la fenêtre reprend ce
  // que le client vient de taper à l'étape « Coordonnées ». Il ne retape ni son
  // nom ni son adresse e-mail, il n'a que son mot de passe à choisir.
  const [firstName, setFirstName] = useState(prefill?.firstName ?? '');
  const [lastName, setLastName] = useState(prefill?.lastName ?? '');
  const [email, setEmail] = useState(prefill?.email ?? pendingEmail ?? '');
  const [phone, setPhone] = useState(prefill?.phone ?? '');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);
  // SC-781: this one error has an action attached — the client cannot type
  // his way in, he has to be sent the link. A banner alone would be a wall.
  const [offerSetupLink, setOfferSetupLink] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<TurnstileWidgetHandle>(null);
  const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY ?? '';
  // Turnstile is only enforced when a site key is configured. Without one
  // (dev / preprod / CI) the widget can never produce a token and the backend
  // fails open, so skip the captcha entirely — mirrors CheckoutPage /
  // ReservationPage. (SC-234: registration was the only flow missing this
  // gate, leaving "Créer mon compte" permanently disabled on preprod.)
  const needCaptcha = !!recaptchaSiteKey;

  const validate = (emailValue = email) => {
    const e: Record<string, string> = {};
    if (firstName.trim().length < 2) e.firstName = t('auth.validation.firstNameRequired');
    if (lastName.trim().length < 2) e.lastName = t('auth.validation.lastNameRequired');
    if (!EMAIL_RE.test(emailValue)) e.email = t('auth.validation.emailInvalid');
    if (phone.trim().length < 6) e.phone = t('auth.validation.phoneRequired');
    if (password.length < 8) e.password = t('auth.validation.passwordMin8');
    if (confirm !== password) e.confirm = t('auth.validation.passwordMismatch');
    if (needCaptcha && !recaptchaToken) e.recaptcha = t('auth.validation.recaptchaRequired');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    setBanner(null);
    if (!restaurantUuid) {
      setBanner(t('auth.missingRestaurant'));
      return;
    }
    const trimmedEmail = email.trim();
    setEmail(trimmedEmail);
    dispatch(setPendingEmail(trimmedEmail));
    if (!validate(trimmedEmail)) return;
    setSubmitting(true);
    try {
      await dispatch(
        registerUser({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: trimmedEmail,
          phone,
          password,
          restaurant_uuid: restaurantUuid,
          recaptcha_token: recaptchaToken ?? '',
        })
      ).unwrap();
      dispatch(closeAccountModal());
      // SC-1013 : ouverte depuis une page qui a un travail en cours (le panier
      // de la commande), la fenêtre lui rend la main. Le détour par « Mon
      // compte » ne vaut que pour l'inscription lancée depuis l'en-tête.
      navigate(redirectTo ?? `/${restaurantUuid}/account`);
    } catch (payload) {
      const { code, fieldErrors } = getRegisterError(payload);
      setOfferSetupLink(code === 'password_setup_required');
      const mappedFieldErrors = fieldErrors ? mapServerFieldErrors(fieldErrors, t) : {};
      if (Object.keys(mappedFieldErrors).length > 0) {
        setErrors(mappedFieldErrors);
        setBanner(t('auth.errors.fieldErrors'));
      } else {
        setBanner(t(ERROR_KEYS[code] ?? ERROR_KEYS.unknown));
      }
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-2.5">
      {banner && (
        <div className="px-3 py-2 bg-yellow-100 border-2 border-ink text-ink text-sm rounded-btn-sm">
          {banner}
          {offerSetupLink && (
            <button
              type="button"
              onClick={() => {
                dispatch(setPendingEmail(email.trim()));
                dispatch(setMode('forgot'));
              }}
              className="block mt-1.5 bg-transparent border-0 p-0 text-sm text-ink underline cursor-pointer"
            >
              {t('auth.requestNewLink')}
            </button>
          )}
        </div>
      )}
      <Field label={t('auth.firstNameLabel')} error={errors.firstName}>
        <input value={firstName} onChange={e => setFirstName(e.target.value)}
          className={`w-full px-3 py-2 bg-paper border-2 ${errors.firstName ? 'border-red-600' : 'border-ink'} rounded-btn-sm text-sm`} />
      </Field>
      <Field label={t('auth.lastNameLabel')} error={errors.lastName}>
        <input value={lastName} onChange={e => setLastName(e.target.value)}
          className={`w-full px-3 py-2 bg-paper border-2 ${errors.lastName ? 'border-red-600' : 'border-ink'} rounded-btn-sm text-sm`} />
      </Field>
      <Field label={t('auth.emailLabel')} error={errors.email}>
        <input type="email" autoComplete="email" value={email}
          onChange={e => { setEmail(e.target.value); dispatch(setPendingEmail(e.target.value)); }}
          className={`w-full px-3 py-2 bg-paper border-2 ${errors.email ? 'border-red-600' : 'border-ink'} rounded-btn-sm text-sm`} />
      </Field>
      <Field label={t('auth.phoneLabel')} error={errors.phone}>
        <input type="tel" autoComplete="tel" value={phone} onChange={e => setPhone(e.target.value)}
          className={`w-full px-3 py-2 bg-paper border-2 ${errors.phone ? 'border-red-600' : 'border-ink'} rounded-btn-sm text-sm`} />
      </Field>
      <Field label={t('auth.passwordLabel')} error={errors.password}>
        <PasswordField value={password} onChange={setPassword} autoComplete="new-password" invalid={!!errors.password} />
      </Field>
      <Field label={t('auth.confirmPasswordLabel')} error={errors.confirm}>
        <PasswordField value={confirm} onChange={setConfirm} autoComplete="new-password" invalid={!!errors.confirm} />
      </Field>
      {needCaptcha && (
        <div className="flex flex-col items-center pt-1" data-testid="recaptcha-slot">
          <TurnstileWidget
            ref={recaptchaRef}
            siteKey={recaptchaSiteKey}
            onSuccess={token => setRecaptchaToken(token)}
            onExpire={() => setRecaptchaToken(null)}
            onError={() => setRecaptchaToken(null)}
          />
          {errors.recaptcha && (
            <span className="text-xs text-red-600 mt-1">{errors.recaptcha}</span>
          )}
        </div>
      )}
      <button type="submit" disabled={submitting || (needCaptcha && !recaptchaToken)}
        className="w-full py-2.5 bg-ink text-paper font-display rounded-btn shadow-stamp-sm cursor-pointer transition-transform duration-200 hover:-translate-y-[1px] hover:-translate-x-[1px] disabled:opacity-60 disabled:hover:transform-none">
        {submitting ? t('auth.registering') : t('auth.registerCta')}
      </button>
      {isGoogleLoginEnabled() && (
        <>
          <div className="flex items-center gap-3 text-ink/50 text-xs pt-1">
            <hr className="flex-1 border-t border-ink/20" />
            <span>{t('auth.orContinueWith')}</span>
            <hr className="flex-1 border-t border-ink/20" />
          </div>
          <GoogleButton />
        </>
      )}
    </form>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col">
      <span className="text-xs font-sans font-semibold text-ink mb-1">{label}</span>
      {children}
      {error && <span className="text-xs text-red-600 mt-0.5">{error}</span>}
    </label>
  );
}
